// pages/Home.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../app-config";
import BannerCarousel from "../components/banner-carousel";
import SidebarHeader from "../components/SidebarHeader";
import BottomNav from "../components/bottom-menu";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";
import Bmnemonic_PNG from "../assets/image/Bmnemonic_PNG.png";
import { FaMapMarkerAlt, FaSpinner, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import { MdLocationOn, MdGpsFixed } from 'react-icons/md';
import { HiLocationMarker } from 'react-icons/hi';
import { PaymentHandler } from "../components/paymentHandler";
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;

const quickAmounts = [10, 20, 30, 50, 100, 250, 350, 500, 1000, 2000];

function AddMoney() {
    const navigate = useNavigate();
    const [amount, setAmount] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [userLocation, setUserLocation] = useState({
        address: { city: '', state: '', country: '' },
        latitude: null,
        longitude: null,
        altitude: null,
        isLoading: true,
        error: null
    });

    const [paymentResponse, setPaymentResponse] = useState(null);

    // Get location on mount
    useEffect(() => {
        getLocation();
    }, []);

    const getLocation = () => {
        setLocationLoading(true);
        setUserLocation(prev => ({ ...prev, isLoading: true }));

        // Check for React Native WebView
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude, altitude } = position.coords;
                    try {
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
                        );
                        const data = await response.json();
                        setUserLocation({
                            address: {
                                city: data.address?.city || data.address?.town || data.address?.village || '',
                                state: data.address?.state || '',
                                country: data.address?.country || ''
                            },
                            latitude,
                            longitude,
                            altitude: altitude || null,
                            isLoading: false,
                            error: null
                        });
                        setLocationLoading(false);
                    } catch (error) {
                        setUserLocation({
                            address: { city: '', state: '', country: '' },
                            latitude,
                            longitude,
                            altitude: altitude || null,
                            isLoading: false,
                            error: 'Failed to get address details'
                        });
                        setLocationLoading(false);
                        toast.error("Failed to get address details");
                    }
                },
                (error) => {
                    setUserLocation({
                        address: { city: '', state: '', country: '' },
                        latitude: null,
                        longitude: null,
                        altitude: null,
                        isLoading: false,
                        error: error.message
                    });
                    setLocationLoading(false);
                    toast.error("Location access denied. Please enable location services.");
                },
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            );
        } else {
            setUserLocation({
                address: { city: '', state: '', country: '' },
                latitude: null,
                longitude: null,
                altitude: null,
                isLoading: false,
                error: 'Geolocation is not supported'
            });
            setLocationLoading(false);
            toast.error("Geolocation is not supported");
        }
    };

    const handleChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, "");
        setAmount(val);
        if (error) setError("");
    };

    const handleAddMoney = async () => {
        // Validate amount first
        if (!amount || amount < 10) {
            setError("Minimum amount is ₹10");
            return;
        }
        if (amount > 10000) {
            setError("Maximum amount is ₹10,000");
            return;
        }
        setError("");
        setLoading(true);

        try {
            const result = await apiCall(
                "payment",
                {
                    amount: String(amount),
                    platform: 1,
                    longitude: userLocation.longitude || null,
                    latitude: userLocation.latitude || null,
                    address: userLocation.address?.city || null
                },
                true
            );

            if (result.status === true) {
                setPaymentResponse(result);
            } else {
                toast.error(result.message || "Failed to fetch bill");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const selectedAmount = (amt) => {
        setAmount(String(amt));
        setError("");
        // Automatically proceed with payment after selecting amount
        setTimeout(() => {
            handleAddMoney();
        }, 100);
    };

    const handlePaymentComplete = (data) => {
        toast.success(`Payment successful of amount ${data.amount}`)
    }

    const handlePaymentError = (error) => {
        toast.error(error)
    }

    return (
        <SidebarHeader>
            <div className="flex items-center gap-3 pb-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                >
                    <FiArrowLeft className="text-xl" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800">
                    Add Money
                </h1>
            </div>
            <div className="max-w-7xl mx-auto mb-16">
                <div className="flex-1 flex flex-col">
                    {/* Suggestions at Top */}
                    <div className="mb-6">
                        <div className="text-sm font-semibold mb-3 pl-1" style={{ color: COLORS.textSecondary }}>
                            Suggested amount
                        </div>
                        <div className="flex gap-2.5 flex-wrap">
                            {quickAmounts.map((amt) => (
                                <button
                                    key={amt}
                                    onClick={() => selectedAmount(amt)}
                                    className="py-2.5 px-5 rounded-xl text-sm font-bold cursor-pointer transition-all duration-200 active:scale-95"
                                    style={{
                                        border: `1.5px solid ${Number(amount) === amt ? COLORS.primaryColor : COLORS.borderColor}`,
                                        background: Number(amount) === amt ? COLORS.primaryLight : COLORS.surfaceColor,
                                        color: Number(amount) === amt ? COLORS.primaryColor : COLORS.textPrimary,
                                    }}
                                >
                                    ₹{amt}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <div
                                className="flex items-center overflow-hidden transition-colors duration-200 p-1 rounded-xl"
                                style={{
                                    border: `1.5px solid ${error ? COLORS.errorColor : COLORS.borderColor}`,
                                    background: COLORS.surfaceColor,
                                }}
                            >
                                <div className="px-3 py-2 text-lg font-bold" style={{ color: COLORS.primaryColor }}>₹</div>
                                <input
                                    type="tel"
                                    placeholder="Enter custom amount"
                                    value={amount}
                                    onChange={handleChange}
                                    className="flex-1 py-2 border-none text-lg font-semibold outline-none bg-transparent"
                                    style={{ color: COLORS.textPrimary }}
                                    onFocus={(e) => (e.target.parentElement.style.borderColor = COLORS.primaryColor)}
                                    onBlur={(e) => (e.target.parentElement.style.borderColor = error ? COLORS.errorColor : COLORS.borderColor)}
                                />
                            </div>
                        </div>
                        {/* Add Money Button */}
                        <button
                            onClick={handleAddMoney}
                            disabled={loading}
                            className="w-full h-13 min-h-12 border-none rounded-lg text-[15px] font-semibold cursor-pointer transition-all duration-200 active:scale-98 flex items-center justify-center gap-2 px-4 leading-none"
                            style={{
                                background:
                                    (loading)
                                        ? COLORS.primaryDark
                                        : COLORS.primaryColor,
                                color: COLORS.textOnPrimary,
                                borderRadius: COLORS.borderRadius,
                                boxShadow: `0 4px 8px ${COLORS.primaryColor}22`,
                                opacity: (loading) ? 0.7 : 1,
                                cursor:
                                    (loading)
                                        ? 'not-allowed'
                                        : 'pointer',
                            }}
                        >
                            {(loading) ? (
                                <>
                                    <svg
                                        className="animate-spin h-5 w-5 shrink-0"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>

                                    <span>
                                        Processing...
                                    </span>
                                </>
                            ) : (
                                <span>Add Money to Wallet</span>
                            )}
                        </button>
                    </div>

                    <div className="mb-4">
                        {error && (
                            <div className="text-xs mt-1 pl-1" style={{ color: COLORS.errorColor }}>
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Warning Note */}
                    <div
                        className="mb-4 p-3 rounded-lg text-xs"
                        style={{
                            border: `1.5px solid ${COLORS.errorColor}`,
                            background: `${COLORS.errorColor}08`,
                            color: COLORS.errorColor,
                        }}
                    >
                        <div className="flex items-start gap-2">
                            <span className="flex-shrink-0 mt-0.5">⚠️</span>
                            <span>
                                The amount in wallet is only for use in Mobile Recharge, DTH Recharge and Google Play Redeem code and will not be permitted for any bill payment.
                            </span>
                        </div>
                    </div>

                    {/* Location Status - Enhanced */}
                    <div className="mb-4">
                        <div
                            className="flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200"
                            style={{
                                background: `linear-gradient(135deg, ${COLORS.primaryColor} 0%, ${COLORS.primaryColor}dd 100%)`,
                                border: `1px solid ${COLORS.borderColor}`,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                        >
                            {/* Left Section - Label with Icon */}
                            <div className="flex items-center gap-2.5">
                                <div className="p-1.5 bg-white/20 rounded-full">
                                    <MdLocationOn className="text-white text-sm" />
                                </div>
                                <span className="text-white text-xs font-medium tracking-wide">
                                    Location
                                </span>
                            </div>

                            {/* Right Section - Dynamic Content */}
                            <div className="flex items-center gap-2">
                                {userLocation.latitude ? (
                                    <>
                                        <div className="text-right">
                                            <div className="text-white text-xs font-medium leading-tight">
                                                {userLocation.address.city}
                                            </div>
                                            <div className="text-white/80 text-[10px] leading-tight">
                                                {userLocation.address.state}
                                            </div>
                                        </div>
                                    </>
                                ) : userLocation.isLoading ? (
                                    <>
                                        <span className="text-white/90 text-xs font-medium">Fetching...</span>
                                        <FaSpinner className="text-white text-sm animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        <span className="text-white/80 text-xs">Unavailable</span>
                                        <FaExclamationCircle className="text-yellow-300 text-sm" />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toaster />

            {/* Location Loading Overlay */}
            {locationLoading && (
                <>
                    <div
                        className="fixed inset-0 backdrop-blur-md bg-black/40 z-[1200] animate-fadeIn"
                        style={{ backdropFilter: 'blur(8px)' }}
                    />
                    <div className="fixed inset-0 z-[1201] flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl animate-scaleIn max-w-[320px] w-full mx-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-4 animate-spin"
                                    style={{ borderTopColor: COLORS.primaryColor }} />
                            </div>
                            <div className="mt-4 text-center">
                                <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                                    Fetching Location
                                </h3>
                                <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
                                    Please allow location access
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {paymentResponse && (
                <PaymentHandler
                    result={paymentResponse}
                    onPaymentComplete={handlePaymentComplete}
                    onPaymentError={handlePaymentError}
                />
            )}

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                    .active\\:scale-95:active {
                        transform: scale(0.95);
                    }
                    .active\\:scale-90:active {
                        transform: scale(0.9);
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { transform: translateY(100%); }
                        to { transform: translateY(0); }
                    }
                    @keyframes scaleIn {
                        from { 
                            opacity: 0;
                            transform: scale(0.9);
                        }
                        to { 
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    .animate-spin {
                        animation: spin 0.8s linear infinite;
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.2s ease forwards;
                    }
                    .animate-slideUp {
                        animation: slideUp 0.3s ease forwards;
                    }
                    .animate-scaleIn {
                        animation: scaleIn 0.3s ease forwards;
                    }
                `}
            </style>
        </SidebarHeader>
    );
}

export default AddMoney;